import React from "react";
import { useTranslation } from "react-i18next";
import { getS3URL } from "../../../_helpers";
import logo from "../../../assets/icons/logo.png";
import { useNavigate } from "react-router-dom";
import moment from "moment/moment";
import {
  BookOpen, Calendar, CheckCircle, Clock, Download,
  Star, Users, ChevronRight, Award, ShieldCheck, Zap, Globe, Sparkles,
} from "lucide-react";
import "./ProgramDetails.custom.css";

const ProgramDetails = ({
  propSlug,
  course,
  fetchCourse,
  loggedIn,
  courses,
  user,
  modalOpen,
  setModalOpen,
  modalOpen1,
  setModalOpen1,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const futureDate = new Date();
  futureDate.setDate(futureDate.getDate() + 30);

  const getDiscountPercent = () => {
    // Check if the course has an explicit DiscountPercent field
    const explicitPercent = courses?.DiscountPercent || courses?.discountPercent || course?.DiscountPercent || course?.discountPercent;
    if (explicitPercent && Number(explicitPercent) > 0) {
      return Number(explicitPercent);
    }
    
    // Otherwise, check if we can calculate it from EnrollmentFee (compared to standard fee+5000)
    const fee = Number(course?.EnrollmentFee || courses?.EnrollmentFee || 0);
    if (fee > 0) {
      const original = fee + 5000;
      return Math.round((5000 / original) * 100);
    }
    return 0;
  };

  const discountPercent = getDiscountPercent();

  return (
    <div className={`w-full mx-auto px-4 sm:px-6 lg:px-8 pb-10 ${propSlug ? "pt-2" : "pt-28"}`}>

      {/* ── Breadcrumb ── */}
      <nav className="mb-8 max-w-7xl mx-auto">
        <ol className="flex items-center text-sm text-gray-400 whitespace-nowrap overflow-x-auto gap-2">
          <li
            className="cursor-pointer hover:text-blue-600 transition-colors font-medium"
            onClick={() => navigate("/")}
          >
            Home
          </li>
          <ChevronRight className="w-3.5 h-3.5 flex-shrink-0" />
          <li className="cursor-pointer hover:text-blue-600 transition-colors font-medium">
            {course.category}
          </li>
          <ChevronRight className="w-3.5 h-3.5 flex-shrink-0" />
          <li className="text-gray-700 font-semibold truncate max-w-xs">{course.CourseName}</li>
        </ol>
      </nav>

      {/* ── Hero ── */}
      <section id="overviews" className="max-w-7xl mx-auto">
        <div className="pd-hero-wrapper">
          {/* Ambient 3D floating glows */}
          <div className="ambient-glow-wrapper">
            <div className="ambient-glow-1"></div>
            <div className="ambient-glow-2"></div>
          </div>

          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center relative z-10">

            {/* Left Column */}
            <div className="lg:col-span-7 space-y-7 relative z-10">

              {/* Live badge + Institution */}
              <div className="flex flex-wrap items-center gap-3">
                <span className="pd-badge">
                  <span className="pd-badge-dot" />
                  <Sparkles size={11} className="text-blue-500 animate-pulse" />
                  Enrolling Now
                </span>
                <div className="flex items-center gap-3 bg-white/60 backdrop-blur-md border border-gray-100/80 px-3 py-1.5 rounded-2xl shadow-sm">
                  <div className="bg-yellow-400 p-1.5 rounded-xl shadow-inner">
                    <img src={logo} alt="yslearn" className="h-6 w-6 object-contain brightness-0" />
                  </div>
                  <div className="leading-tight">
                    <p className="text-[9px] font-black text-gray-400 tracking-[0.15em] uppercase">
                      yslearn Engineering
                    </p>
                    <p className="text-[12px] font-black text-blue-900 tracking-wider uppercase">
                      Professional Education
                    </p>
                  </div>
                </div>
              </div>

              {/* Title */}
              <div>
                <h1 className="text-4xl lg:text-[52px] font-black text-gray-900 leading-[1.1] tracking-tight">
                  <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-700 via-indigo-700 to-purple-800">
                    {course.CourseName}
                  </span>
                </h1>
                <div className="title-underline" />
              </div>

              {/* Tagline & Description */}
              <div className="space-y-3">
                <p className="text-lg font-bold text-gray-800">
                  Harness {course.CourseName} To Accelerate Your Career Transformation
                </p>
                <div className="text-[15px] text-gray-500 leading-relaxed max-w-2xl line-clamp-4 bg-white/40 backdrop-blur-sm p-4 rounded-2xl border border-gray-100/50">
                  <div dangerouslySetInnerHTML={{ __html: course?.Description }} />
                </div>
              </div>

              {/* Trust strip */}
              <div className="pd-trust-strip">
                <div className="pd-trust-item">
                  <div className="flex-shrink-0 w-8 h-8 bg-green-50 rounded-lg flex items-center justify-center text-green-600 shadow-inner">
                    <ShieldCheck className="w-4 h-4" />
                  </div>
                  <span>Industry Certified</span>
                </div>
                <div className="pd-trust-item">
                  <div className="flex-shrink-0 w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center text-blue-600 shadow-inner">
                    <Award className="w-4 h-4" />
                  </div>
                  <span>Job-Ready Curriculum</span>
                </div>
                <div className="pd-trust-item">
                  <div className="flex-shrink-0 w-8 h-8 bg-purple-50 rounded-lg flex items-center justify-center text-purple-600 shadow-inner">
                    <Globe className="w-4 h-4" />
                  </div>
                  <span>Live Online</span>
                </div>
                <div className="pd-trust-item">
                  <div className="flex-shrink-0 w-8 h-8 bg-yellow-50 rounded-lg flex items-center justify-center text-yellow-600 shadow-inner">
                    <Zap className="w-4 h-4" />
                  </div>
                  <span>Placement Support</span>
                </div>
              </div>

              {/* Collaboration */}
              <div className="flex items-center gap-3">
                <span className="text-[11px] font-black text-gray-400 uppercase tracking-widest">
                  In Collaboration With
                </span>
                <div className="pd-collab-pill bg-white/80 border border-gray-100/80 shadow-sm rounded-full py-1.5 pl-1.5 pr-4 flex items-center gap-2.5">
                  <div className="w-6 h-6 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center text-white font-extrabold text-[9px] shadow-sm">
                    AZ
                  </div>
                  <span className="font-extrabold text-gray-700 text-xs tracking-wide">Microsoft Azure</span>
                </div>
              </div>

            </div>

            {/* Right Column – Image */}
            <div className="lg:col-span-5 relative mt-8 lg:mt-0 hero-image-perspective relative z-10">
              <div className="hero-decorative-bg shadow-2xl" />
              <div className="hero-image-container">
                {discountPercent > 0 && (
                  <div className="discount-ribbon-wrapper">
                    <span className="discount-ribbon">{discountPercent}% OFF</span>
                  </div>
                )}
                <img
                  src={getS3URL(courses?.imageFiles[0]?.fileName)}
                  alt="Course Preview"
                  loading="lazy"
                  className="w-full aspect-[4/3] object-cover"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src =
                      "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=800&h=600&fit=crop";
                  }}
                />

                {/* Floating stats */}
                <div className="rating-floating-card">
                  <div className="flex items-center gap-2">
                    <Star className="w-6 h-6 text-yellow-400 fill-yellow-400" />
                    <div>
                      <p className="font-black text-gray-900 text-xl leading-none">4.5</p>
                      <p className="text-[10px] text-gray-400 uppercase tracking-widest font-bold mt-0.5">
                        Rating
                      </p>
                    </div>
                  </div>
                  <div className="w-px h-10 bg-gray-100" />
                  <div className="flex items-center gap-2">
                    <Users className="w-6 h-6 text-blue-600" />
                    <div>
                      <p className="font-black text-gray-900 text-xl leading-none">2,000+</p>
                      <p className="text-[10px] text-gray-400 uppercase tracking-widest font-bold mt-0.5">
                        Learners
                      </p>
                    </div>
                  </div>
                  <div className="w-px h-10 bg-gray-100" />
                  <div className="flex items-center gap-2">
                    <Award className="w-6 h-6 text-purple-500" />
                    <div>
                      <p className="font-black text-gray-900 text-xl leading-none">98%</p>
                      <p className="text-[10px] text-gray-400 uppercase tracking-widest font-bold mt-0.5">
                        Placed
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ── CTA Buttons & Info Bar Row ── */}
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 pt-8 border-t border-gray-100/60 mt-12 relative z-10">
            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 flex-shrink-0">
              {!loggedIn ? (
                <button
                  onClick={() => navigate("/register")}
                  className="btn-primary-custom text-base px-10"
                >
                  Apply Now
                </button>
              ) : (
                user.role === 2 &&
                (!courses?.isPurchased ? (
                  <button
                    onClick={() => setModalOpen1(true)}
                    className="btn-primary-custom text-base px-10"
                  >
                    Apply Now
                  </button>
                ) : (
                  <button className="btn-primary-custom text-base px-10 !bg-gradient-to-r !from-green-500 !to-emerald-600 !shadow-green-200">
                    <CheckCircle className="w-5 h-5" />
                    Enrolled
                  </button>
                ))
              )}

              <a
                href={
                  loggedIn && courses?.courseFiles?.length > 0
                    ? `https://pub-561e50d39a1c4c3a9c5d9a7715790c10.r2.dev/${courses?.courseFiles[0]?.fileName}`
                    : "#"
                }
                className="btn-outline-custom text-base px-10"
                target={loggedIn ? "_blank" : "_self"}
                rel="noopener noreferrer"
                onClick={(e) => {
                  if (!loggedIn) {
                    e.preventDefault();
                    navigate("/register");
                  }
                }}
              >
                <Download className="w-5 h-5" />
                Download Syllabus
              </a>
            </div>

            {/* Info Bar */}
            <div className="flex flex-wrap items-center gap-4 sm:gap-6 lg:ml-auto">
              
              <div className="pd-info-card">
                <div className="pd-info-icon-wrap">
                  <Clock className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-[10px] text-gray-400 uppercase tracking-[0.1em] font-extrabold leading-none">
                    Application Closes
                  </p>
                  <p className="font-black text-gray-900 text-[13px] mt-1 leading-none">
                    {moment(futureDate).format("D MMM, YYYY")}
                  </p>
                </div>
              </div>

              <div className="pd-info-card">
                <div className="pd-info-icon-wrap">
                  <Calendar className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-[10px] text-gray-400 uppercase tracking-[0.1em] font-extrabold leading-none">
                    Program Duration
                  </p>
                  <p className="font-black text-gray-900 text-[13px] mt-1 leading-none">
                    {course.Duration} Hours
                  </p>
                </div>
              </div>

              <div className="pd-info-card">
                <div className="pd-info-icon-wrap">
                  <BookOpen className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-[10px] text-gray-400 uppercase tracking-[0.1em] font-extrabold leading-none">
                    Learning Format
                  </p>
                  <p className="font-black text-gray-900 text-[13px] mt-1 leading-none">
                    {course.courseType}
                  </p>
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProgramDetails;